import { useRef, useState } from 'react'
import classnames from 'classnames'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getBasicDetailsKey, attachProfileImage } from '../../api-client/account'
import isFileSizeValid from '../../utils/is-file-size-valid'
import MessageModal from '../../components/message-modal'
import Loader from '../../components/loader'
import AvatarSvg from '../svg/avatar'
import { useAuth } from '../../contexts/auth'
import Avatar from '../avatar'
import DealerAvatar from '../svg/avatar-dealer'

export default function AccountAvatar() {
  const fileRef = useRef()
  const queryClient = useQueryClient()
  const [isImageSizeErrorModalOpen, setIsImageSizeErrorModalOpen] = useState(false)
  const [{ userDetails }] = useAuth()

  const { mutate, isLoading } = useMutation({
    mutationFn: attachProfileImage,
    onSuccess: () => queryClient.invalidateQueries(getBasicDetailsKey),
  })

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (isFileSizeValid({ file })) {
      mutate({ image: file })
    } else {
      setIsImageSizeErrorModalOpen(true)
    }
  }

  return (
    <div className={classnames('accountAvatar', { loading: isLoading })}>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Avatar
            alt='user avatar'
            src={userDetails.profileImageUrl}
            height={180}
            width={180}
            fallbackComponent={userDetails.isDealer ? DealerAvatar : AvatarSvg}
          />

          <button className='flatBtn edit' onClick={() => fileRef.current.click()}>
            Edit
          </button>
        </>
      )}
      <input
        ref={fileRef}
        type='file'
        accept='image/jpg, image/jpeg, image/png'
        onChange={handleAvatarChange}
      />

      {isImageSizeErrorModalOpen && (
        <MessageModal
          title='Image size error'
          text='Image size should be less than 10mb'
          onClose={() => setIsImageSizeErrorModalOpen(false)}
        />
      )}

      <style jsx>{`
        .accountAvatar {
          position: relative;
          width: 18rem;
          height: 18rem;
          margin: 0 auto;
          border-radius: 50%;
          overflow: hidden;

          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            box-shadow: 0 0 5rem 1rem #000;
            z-index: 1;
          }

          &.loading::after {
            box-shadow: none;
          }

          button.edit {
            position: absolute;
            bottom: 1.1rem;
            left: 50%;
            transform: translate(-50%, 0);
            z-index: 2;
            color: #fff;
            font-size: 1.6rem;
            font-weight: 700;
          }

          :global(img) {
            width: 100%;
            height: 100%;
            margin: 0 auto;
            object-fit: cover;
            object-position: center;
          }

          :global(svg) {
            width: 100%;
            height: 100%;
          }

          :global(.loaderSVG) {
            width: 70%;
            height: 70%;
          }

          input {
            visibility: hidden;
          }
        }

        @media (max-width: 1024px) {
          .accountAvatar {
            width: 10rem;
            height: 10rem;

            &::after {
              display: none;
            }

            button.edit {
              bottom: 0.7rem;
              font-size: 1.2rem;
            }
          }
        }
      `}</style>
    </div>
  )
}
